'use client'

import { useState, useEffect, useMemo } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { signUpWithCredentials, signInWithGoogle, signInWithFacebook, resendVerificationEmail } from '@/lib/auth-actions'
import { Loader2, Mail, CheckCircle, RefreshCw, Gift, Users, Eye, EyeOff } from 'lucide-react'
import { Logo } from '@/components/ui/logo'
import { Checkbox } from '@/components/ui/checkbox'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { SystemMessageBanner } from '@/components/system-message'
import { getPromoCodeCookie, setPromoCodeCookie } from '@/lib/promo-cookie'
import { setPlanIntentCookie } from '@/lib/plan-intent-cookie'
import { trackSignupComplete } from '@/lib/analytics'
import { useTranslations } from 'next-intl'

interface SignUpFormProps {
  googleEnabled: boolean
  facebookEnabled: boolean
}

export default function SignUpForm({ googleEnabled, facebookEnabled }: SignUpFormProps) {
  const t = useTranslations('auth.signup')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState<string>('')
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl') || undefined

  // Plan intent — capture ?plan=pro_monthly or ?plan=pro_annual and persist to cookie
  const planParam = searchParams.get('plan') || ''
  useEffect(() => {
    if (planParam === 'pro_monthly' || planParam === 'pro_annual') {
      setPlanIntentCookie(planParam)
    }
  }, [planParam])

  // Promo/Referral code - prioritize URL over cookie (initialize once)
  const refCode = searchParams.get('ref') || searchParams.get('promo') || ''
  const [promoCode, setPromoCode] = useState(() => {
    // Initialize with URL param if present, otherwise try cookie
    if (refCode) return refCode
    // Try to get from cookie (will only work client-side)
    if (typeof window !== 'undefined') {
      return getPromoCodeCookie() || ''
    }
    return ''
  })

  // Store URL param to cookie for persistence
  useEffect(() => {
    if (refCode) {
      setPromoCodeCookie(refCode)
    }
  }, [refCode])

  const hasOAuthProviders = googleEnabled || facebookEnabled

  // Manual validation trigger for promo/referral codes
  const [shouldValidateCode, setShouldValidateCode] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Validate promo/referral code - ONLY when manually triggered
  const validateReferralQuery = trpc.referral.validateReferralCode.useQuery(
    { code: promoCode },
    {
      enabled: shouldValidateCode && promoCode.length >= 4,
      retry: false, // Don't auto-retry on error
    }
  )
  const validatePromoQuery = trpc.promotion.validateCode.useQuery(
    { code: promoCode },
    {
      enabled: shouldValidateCode && promoCode.length >= 4 && !validateReferralQuery.data?.valid,
      retry: false, // Don't auto-retry on error
    }
  )

  // Handle validation state updates
  useEffect(() => {
    if (shouldValidateCode) {
      // Reset trigger when queries complete (success or error)
      if (!validateReferralQuery.isLoading && !validatePromoQuery.isLoading) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing validation state with tRPC query results
        setShouldValidateCode(false)

        // Handle errors
        if (validatePromoQuery.error) {
          if (validatePromoQuery.error.data?.code === 'TOO_MANY_REQUESTS') {
            setValidationError(validatePromoQuery.error.message)
          } else {
            setValidationError(t('promoCodeValidationError'))
          }
        } else if (validateReferralQuery.data?.valid || validatePromoQuery.data?.valid) {
          setValidationError(null)
        }
      }
    }
  }, [shouldValidateCode, validateReferralQuery.isLoading, validateReferralQuery.data, validatePromoQuery.isLoading, validatePromoQuery.data, validatePromoQuery.error, t])

  // Derive code type directly from validation results (no syncing needed)
  const codeType = useMemo((): 'referral' | 'promo' | null => {
    if (validateReferralQuery.data?.valid) return 'referral'
    if (validatePromoQuery.data?.valid) return 'promo'
    return null
  }, [validateReferralQuery.data?.valid, validatePromoQuery.data?.valid])

  const isValidCode = codeType !== null
  const isCheckingCode = validateReferralQuery.isLoading || validatePromoQuery.isLoading

  // Reset validation state when code changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting validation when input changes
    setValidationError(null)
  }, [promoCode])

  // Handle manual code validation
  const handleCheckCode = () => {
    if (promoCode.length < 4) {
      setValidationError(t('promoCodeMinLength'))
      return
    }
    setValidationError(null)
    setShouldValidateCode(true)
  }

  // Handle resend verification email with rate limiting
  async function handleResendVerification() {
    if (resendCooldown > 0 || !submittedEmail) return

    setIsResending(true)
    setResendSuccess(false)

    const result = await resendVerificationEmail(submittedEmail, callbackUrl)

    if (result?.error) {
      setError(result.error)
    } else if (result?.success) {
      setResendSuccess(true)
      // Start 60 second cooldown to prevent spam
      setResendCooldown(60)
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    setIsResending(false)
  }

  async function handleSubmit(formData: FormData) {
    setIsLoading(true)
    setError(null)
    setPasswordError(null)

    const password = formData.get('password') as string
    const confirmPassword = formData.get('confirmPassword') as string

    // Validate passwords match
    if (password !== confirmPassword) {
      setPasswordError(t('passwordsDoNotMatch'))
      setIsLoading(false)
      return
    }

    const email = formData.get('email') as string
    setSubmittedEmail(email)

    const result = await signUpWithCredentials(formData, callbackUrl)

    if (result?.error) {
      setError(result.error)
      setIsLoading(false)
    } else if (result?.success && result?.message === 'verification_email_sent') {
      // Track successful signup initiation
      trackSignupComplete('email')
      setVerificationEmailSent(true)
      setIsLoading(false)
    }
  }

  async function handleGoogleSignIn() {
    await signInWithGoogle(callbackUrl)
  }

  async function handleFacebookSignIn() {
    await signInWithFacebook(callbackUrl)
  }

  // Build login URL with callback if present
  const loginUrl = callbackUrl
    ? `/login?callbackUrl=${encodeURIComponent(callbackUrl)}`
    : '/login'

  // Show verification email sent screen
  if (verificationEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-4">
        <Card className="w-full max-w-md border-zinc-700 bg-zinc-900/80 backdrop-blur">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-4 rounded-full bg-emerald-500/20">
                <Mail className="h-12 w-12 text-emerald-400" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-white">{t('verificationTitle')}</CardTitle>
            <CardDescription className="text-zinc-400">
              {t('verificationDescription', { email: submittedEmail })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {resendSuccess && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center gap-2">
                <CheckCircle className="h-4 w-4 shrink-0" />
                {t('verificationSent')}
              </div>
            )}
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300">{t('verificationStep1')}</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300">{t('verificationStep2')}</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300">{t('verificationStep3')}</p>
              </div>
            </div>

            {/* Resend verification email button */}
            <div className="pt-2">
              <Button
                type="button"
                variant="outline"
                className="w-full border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                onClick={handleResendVerification}
                disabled={isResending || resendCooldown > 0}
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('resendSending')}
                  </>
                ) : resendCooldown > 0 ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {t('resendCooldown', { seconds: resendCooldown })}
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {t('resendButton')}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-sm text-zinc-400">
              {t('alreadyVerified')}{' '}
              <Link href={loginUrl} className="text-violet-400 hover:text-violet-300 font-medium">
                {t('signIn')}
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-4">
      <div className="w-full max-w-md space-y-4">
        <SystemMessageBanner targetLocation="auth_signup" disablePolling />
        <Card className="border-zinc-700 bg-zinc-900/80 backdrop-blur">
          <CardHeader className="text-center space-y-2">
            <div className="flex justify-center mb-2">
              <Link href="/">
                <Logo size="lg" />
              </Link>
            </div>
            <CardTitle className="text-2xl font-bold text-white">{t('title')}</CardTitle>
          <CardDescription className="text-zinc-400">
            {t('description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <form action={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">{t('nameLabel')}</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder={t('namePlaceholder')}
                required
                autoFocus
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-400"
              />
              <p className="text-xs text-zinc-400">
                {t('nameHint')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">{t('emailLabel')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={t('emailPlaceholder')}
                required
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">{t('passwordLabel')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('passwordPlaceholder')}
                  required
                  minLength={8}
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-400 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-zinc-400">{t('passwordHint')}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-300">{t('confirmPasswordLabel')}</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder={t('passwordPlaceholder')}
                  required
                  minLength={8}
                  className={cn(
                    "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-400 pr-10",
                    passwordError && "border-red-500/50"
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
                  aria-label={showConfirmPassword ? t('hidePasswordConfirmation') : t('showPasswordConfirmation')}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordError && (
                <p className="text-xs text-red-400">{passwordError}</p>
              )}
            </div>

            {/* Promo/Referral Code */}
            <div className="space-y-2">
              <Label htmlFor="promoCode" className="text-zinc-300 flex items-center gap-2">
                {codeType === 'referral' ? <Users className="h-4 w-4" /> : <Gift className="h-4 w-4" />}
                {t('promoCodeLabel')} <span className="text-zinc-400 text-xs">{t('promoCodeOptional')}</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="promoCode"
                  name="promoCode"
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder={t('promoCodePlaceholder')}
                  className={cn(
                    "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-400 uppercase font-mono",
                    isValidCode && "border-green-500/50"
                  )}
                  maxLength={20}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCheckCode}
                  disabled={isCheckingCode || promoCode.length < 4}
                  className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white shrink-0"
                >
                  {isCheckingCode ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : isValidCode ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    t('checkButton')
                  )}
                </Button>
              </div>

              {/* Show validation error */}
              {validationError && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 space-y-1">
                  <p className="text-amber-400 text-sm">{validationError}</p>
                  {validationError.includes('wait') && (
                    <p className="text-xs text-zinc-400">
                      {t('promoCodeRateLimitHint')}
                    </p>
                  )}
                </div>
              )}

              {/* Show referral info */}
              {codeType === 'referral' && validateReferralQuery.data?.referrer && (
                <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 space-y-2">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={validateReferralQuery.data.referrer.profileImageUrl || undefined} />
                      <AvatarFallback className="bg-violet-600 text-white text-sm">
                        {validateReferralQuery.data.referrer.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm text-violet-300">{t('referredBy')}</p>
                      <p className="font-medium text-white">{validateReferralQuery.data.referrer.name}</p>
                    </div>
                  </div>
                  {validateReferralQuery.data.refereePromotion && (
                    <div className="pt-2 border-t border-violet-500/20">
                      <p className="text-xs text-violet-300 mb-1">{t('youllReceive')}</p>
                      <p className="text-sm font-medium text-white">{validateReferralQuery.data.refereePromotion.name}</p>
                      {validateReferralQuery.data.refereePromotion.description && (
                        <p className="text-xs text-zinc-400 mt-1">{validateReferralQuery.data.refereePromotion.description}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Show promo info */}
              {codeType === 'promo' && validatePromoQuery.data?.promotion && (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 space-y-1">
                  <div className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-green-400" />
                    <span className="text-green-300 font-medium text-sm">{validatePromoQuery.data.promotion.name}</span>
                  </div>
                  {validatePromoQuery.data.promotion.description && (
                    <p className="text-xs text-zinc-400 pl-6">{validatePromoQuery.data.promotion.description}</p>
                  )}
                </div>
              )}
            </div>

            {/* Hidden field to pass the validated code and type - only if successfully validated */}
            <input type="hidden" name="validatedCode" value={isValidCode ? promoCode : ''} />
            <input type="hidden" name="codeType" value={codeType || ''} />

            {/* Helper text for optional promo code */}
            {promoCode && !isValidCode && (
              <p className="text-xs text-zinc-400">
                {t('promoCodeTip')}
              </p>
            )}

            {/* Terms and Privacy Agreement */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor="terms" className="text-sm text-zinc-400 leading-relaxed cursor-pointer">
                {t('termsAgreement')}{' '}
                <Link href="/terms" className="text-violet-400 hover:text-violet-300 underline" target="_blank">
                  {t('termsOfService')}
                </Link>{' '}
                {t('and')}{' '}
                <Link href="/privacy" className="text-violet-400 hover:text-violet-300 underline" target="_blank">
                  {t('privacyPolicy')}
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
              disabled={isLoading || !agreedToTerms}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('creatingAccount')}
                </>
              ) : (
                t('createAccountButton')
              )}
            </Button>
          </form>

          {hasOAuthProviders && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full bg-zinc-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-zinc-900 px-2 text-zinc-400">{t('orContinueWith')}</span>
                </div>
              </div>

              <div className={`grid gap-4 ${googleEnabled && facebookEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {googleEnabled && (
                  <form action={handleGoogleSignIn}>
                    <Button
                      type="submit"
                      variant="outline"
                      className="w-full border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Google
                    </Button>
                  </form>
                )}
                {facebookEnabled && (
                  <form action={handleFacebookSignIn}>
                    <Button
                      type="submit"
                      variant="outline"
                      className="w-full border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                    >
                      <svg className="mr-2 h-4 w-4" fill="#1877F2" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </Button>
                  </form>
                )}
              </div>
            </>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-zinc-400">
            {t('alreadyHaveAccount')}{' '}
            <Link href={loginUrl} className="text-violet-400 hover:text-violet-300 font-medium">
              {t('signInLink')}
            </Link>
          </p>
        </CardFooter>
        </Card>
      </div>
    </div>
  )
}
